import {
  ConnectionType,
  type MarketAccountsContextProps,
  type UnifiedLiquidityContextProps,
  useMarketAccountsContext,
  useUnifiedLiquidity,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import {
  getTradableMarketAccountNames,
  type GetTradableMarketAccountNamesParams,
  useTradableMarketAccountNames,
  type UseTradableMarketAccountNamesParams,
} from '../../../hooks/useTradableMarketAccountNames';
import { useTradingSettings } from '../../../providers/TradingSettingsContext';
import type { MarketSelectorItem } from '../types';
import { buildMarketSelectorItems, useBuildMarketSelectorItems } from '../utils/useBuildMarketSelectorItems';
import {
  getOrderMarketSelectorConditions,
  type GetOrderMarketSelectorConditionsParams,
  useOrderMarketSelectorConditions,
} from './useOrderMarketSelectorConditions';

// We have some async MSS conditions which we want to give a second to load before we send out our initial set of
// market selector items. This helps us avoid the case where we default the order form to items which a second later
// turn out to be disabled in the org secmaster, for example
export const ORDER_FORM_WAIT_FOR_CONDITIONS_MS = 1500;

export type UseOrderMarketSelectorItemsParams = Omit<
  UseTradableMarketAccountNamesParams,
  'connectionType' | 'includeOfflineMarkets'
>;

export const useOrderMarketSelectorItems = (params: UseOrderMarketSelectorItemsParams) => {
  const { useNewMarketSelector } = useTradingSettings();
  const { marketAccountsByName } = useMarketAccountsContext();

  const availableMarketAccountNames = useTradableMarketAccountNames({
    ...params,
    connectionType: ConnectionType.Orders,
  });

  const marketAccounts = useMemo(
    () => compact(availableMarketAccountNames.map(name => marketAccountsByName.get(name))),
    [availableMarketAccountNames, marketAccountsByName]
  );

  const ulResponse = useUnifiedLiquidity(params.isUnifiedLiquidityEnabled ? params.security?.Symbol : undefined);

  const { conditions } = useOrderMarketSelectorConditions({
    symbol: params.security?.Symbol,
    ulTokensForSymbol: ulResponse?.Tokens,
  });

  return useBuildMarketSelectorItems({
    marketAccounts,
    conditions,
    // This is new market selector behavior
    waitForConditionsMs: useNewMarketSelector ? ORDER_FORM_WAIT_FOR_CONDITIONS_MS : undefined,
    readyToBuildItems: params.security != null,
  });
};

export type GetOrderMarketSelectorItemsParams = Omit<
  GetTradableMarketAccountNamesParams,
  'connectionType' | 'includeOfflineMarkets' | 'combineAllMarketAccounts'
> &
  Omit<GetOrderMarketSelectorConditionsParams, 'ulTokensForSymbol'> & {
    marketAccountsByName: MarketAccountsContextProps['marketAccountsByName'];
    unifiedLiquidityTokenBySymbol: UnifiedLiquidityContextProps['unifiedLiquidityTokenBySymbol'];
  };

export function getOrderMarketSelectorItems({
  marketAccountsByName,
  unifiedLiquidityTokenBySymbol,
  symbol,
  securitiesBySymbol,
  mssByAccountBySymbol,
  enableMSSValidation,
  marketCredentialsWithConnectionStatusByMarketAccountName,
  ...params
}: GetOrderMarketSelectorItemsParams): MarketSelectorItem[] {
  if (!symbol) {
    return [];
  }

  const ulResponse = params.isUnifiedLiquidityEnabled ? unifiedLiquidityTokenBySymbol?.get(symbol) : undefined;
  const tradableMarketAccountNames = getTradableMarketAccountNames({
    ...params,
    ulResponse,
    securitiesBySymbol,
    connectionType: ConnectionType.Orders,
  });

  const tradableMarketAccounts = compact(tradableMarketAccountNames.map(name => marketAccountsByName.get(name)));

  const conditions = getOrderMarketSelectorConditions({
    enableMSSValidation,
    symbol,
    ulTokensForSymbol: ulResponse?.Tokens,
    mssByAccountBySymbol,
    securitiesBySymbol,
    marketCredentialsWithConnectionStatusByMarketAccountName,
  });

  return buildMarketSelectorItems({
    marketAccounts: tradableMarketAccounts,
    conditions,
    marketsByName: params.marketsByName,
  });
}
